/**
 * 舊系統 mdb 匯入(REQ-20260520):
 * 1. 選 .mdb 檔
 * 2. 預覽 → 顯示 4 個 target 的 source / importable / existing 筆數
 * 3. 勾選要匯入的 target → 執行匯入
 * 4. 顯示結果(inserted / skipped / failed)
 *
 * 每次 preview / run 都重新 POST 一次檔案(同一份 .mdb),實作簡單。
 * 40 MB 上傳 ~ 數秒,可接受。
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import type {
  ApiSuccess,
  MdbImportHistoryResponse,
  MdbImportPreviewResult,
  MdbImportRunResult,
  MdbImportRunRow,
  MdbImportTarget,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

/**
 * 從 axios 錯誤抓後端真實 error.message。
 * 不抓到就退回 axios 預設的 "Request failed with status code 500",
 * 再退回到呼叫端給的 fallback。
 */
function extractApiError(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message ?? e.message ?? fallback;
  }
  return e instanceof Error ? e.message : fallback;
}

interface PreviewState {
  filename: string;
  result: MdbImportPreviewResult;
}

export function MdbImportPage(): JSX.Element {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [selected, setSelected] = useState<Set<MdbImportTarget>>(new Set());
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<MdbImportRunResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<MdbImportRunRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  /** REQ-20260521 Phase 3A:展開哪些 target 的未匹配清單 */
  const [unmatchedOpen, setUnmatchedOpen] = useState<Set<MdbImportTarget>>(new Set());

  const loadHistory = async (): Promise<void> => {
    setHistoryLoading(true);
    try {
      const res = await apiClient.get<ApiSuccess<MdbImportHistoryResponse>>('/erp/mdb-import/history');
      setHistory(res.data.data.items);
    } catch {
      // 失敗就靜默,不打擾使用者(主流程沒影響)
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    void loadHistory();
  }, []);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setPreview(null);
    setSelected(new Set());
    setRunResult(null);
    setErr(null);
  };

  const onPreview = async (): Promise<void> => {
    if (!file) return;
    setPreviewing(true);
    setErr(null);
    setRunResult(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const res = await apiClient.post<ApiSuccess<MdbImportPreviewResult>>(
        '/erp/mdb-import/preview',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000 },
      );
      const r = res.data.data;
      setPreview({ filename: file.name, result: r });
      // 預設勾選「有可匯資料且 DB 還沒任何資料」的 target,避免覆蓋(雖然 backend 已用 skip 保護)
      const next = new Set<MdbImportTarget>();
      for (const s of r.summaries) {
        if (s.importableCount > 0 && s.existingCount === 0) next.add(s.target);
      }
      setSelected(next);
    } catch (e) {
      setErr(extractApiError(e, '預覽失敗'));
    } finally {
      setPreviewing(false);
    }
  };

  const toggle = (t: MdbImportTarget): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const toggleUnmatched = (t: MdbImportTarget): void => {
    setUnmatchedOpen((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });
  };

  const onRun = async (): Promise<void> => {
    if (!file || selected.size === 0) return;
    if (!window.confirm(`要把選中的 ${selected.size} 個項目從 mdb 匯入嗎?(已存在的 code 會自動跳過)`)) {
      return;
    }
    setRunning(true);
    setErr(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      // backend 用 split(',') 解 targets
      form.append('targets', Array.from(selected).join(','));
      const res = await apiClient.post<ApiSuccess<MdbImportRunResult>>(
        '/erp/mdb-import/run',
        form,
        // 600s,對齊 Caddy `response_header_timeout`。實測 18K rows 約 6.5 min。
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600_000 },
      );
      setRunResult(res.data.data);
      // 跑完重整歷史列
      void loadHistory();
    } catch (e) {
      // 504 Gateway Timeout:後端可能還在跑,別嚇使用者(實測一次大量匯入會發生)。
      // 給明確提示 + 自動每 30s 重整歷史頁,跑完了就會看到結果。
      if (axios.isAxiosError(e) && e.response?.status === 504) {
        setErr(
          '伺服器處理時間較長,連線已逾時 — 後端仍在處理中,請稍候 30 秒後查看下方「匯入紀錄」確認結果(會自動重整)。',
        );
        // 每 30 秒重整歷史一次,最多 10 分鐘
        let tries = 0;
        const poll = setInterval(() => {
          tries += 1;
          void loadHistory();
          if (tries >= 20) clearInterval(poll);
        }, 30_000);
      } else {
        setErr(extractApiError(e, '匯入失敗'));
      }
    } finally {
      setRunning(false);
    }
  };

  const onClear = (): void => {
    setFile(null);
    setPreview(null);
    setSelected(new Set());
    setRunResult(null);
    setErr(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <section className="grid gap-4 max-w-3xl">
      <h2 className="text-lg font-semibold">舊系統 mdb 匯入</h2>
      <p className="text-xs text-slate-600 leading-relaxed">
        上傳 Microsoft Access 舊系統(<code>.mdb</code>)備份檔,把 Phase 1A 4 個 reference table
        (貨品類別 / 貨品單位 / 廠商類別 / 倉庫)一次性匯入新系統。
        匯入策略:<strong>by code 去重</strong> — 系統內已存在的 code 自動跳過、不會覆蓋手動資料。
        後續 Phase 1B / 1C 上線後會擴充支援廠商與貨品的匯入。
      </p>

      <div className="grid gap-2 bg-white border rounded p-4">
        <label className="text-sm">
          <span className="text-slate-700 block mb-1">選擇 mdb 檔(僅副檔名 .mdb)</span>
          <input
            ref={fileRef}
            type="file"
            accept=".mdb,application/x-msaccess"
            onChange={onPickFile}
            disabled={previewing || running}
            className="text-sm"
          />
        </label>
        {file ? (
          <div className="text-xs text-slate-600">
            已選:<span className="font-mono">{file.name}</span> ({(file.size / 1024 / 1024).toFixed(1)} MB)
          </div>
        ) : null}
        <div className="flex gap-2 items-center pt-1">
          <button
            type="button"
            onClick={() => void onPreview()}
            disabled={!file || previewing || running}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {previewing ? '預覽中…' : '預覽'}
          </button>
          <button
            type="button"
            onClick={onClear}
            disabled={previewing || running}
            className="border rounded px-3 py-1.5 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            清除
          </button>
        </div>
      </div>

      {err ? (
        <div role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {err}
        </div>
      ) : null}

      {preview ? (
        <div className="grid gap-3 bg-white border rounded p-4">
          <div className="text-sm font-medium text-slate-800">
            預覽 — {preview.filename}({(preview.result.fileSize / 1024 / 1024).toFixed(1)} MB)
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-slate-600">
                <th className="px-2 py-1.5 w-10"></th>
                <th className="px-2 py-1.5">目標</th>
                <th className="px-2 py-1.5 w-32">來源表 (mdb)</th>
                <th className="px-2 py-1.5 w-24 text-right">原始筆數</th>
                <th className="px-2 py-1.5 w-24 text-right">可匯入</th>
                <th className="px-2 py-1.5 w-24 text-right">DB 已存在</th>
              </tr>
            </thead>
            <tbody>
              {preview.result.summaries.map((s) => {
                const hasUnmatched = (s.unmatchedPartyCount ?? 0) > 0;
                const isOpen = unmatchedOpen.has(s.target);
                return (
                  <Fragment key={s.target}>
                    <tr className="border-b last:border-b-0 hover:bg-slate-50">
                      <td className="px-2 py-1.5">
                        <input
                          type="checkbox"
                          checked={selected.has(s.target)}
                          onChange={() => toggle(s.target)}
                          disabled={running || s.importableCount === 0}
                        />
                      </td>
                      <td className="px-2 py-1.5">{s.targetLabel}</td>
                      <td className="px-2 py-1.5 font-mono text-xs">{s.sourceTable}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{s.sourceRowCount}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {s.importableCount}
                        {s.importableCount !== s.sourceRowCount ? (
                          <span className="text-slate-400 text-xs"> (過濾後)</span>
                        ) : null}
                        {hasUnmatched ? (
                          <button
                            type="button"
                            onClick={() => toggleUnmatched(s.target)}
                            className="ml-1 text-xs text-red-700 underline-offset-2 hover:underline"
                          >
                            (未匹配 {s.unmatchedPartyCount}{isOpen ? ' ▾' : ' ▸'})
                          </button>
                        ) : null}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {s.existingCount > 0 ? (
                          <span className="text-amber-700">{s.existingCount}</span>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </td>
                    </tr>
                    {hasUnmatched && isOpen ? (
                      <tr className="bg-red-50/40">
                        <td colSpan={6} className="px-3 py-2">
                          <div className="text-xs text-slate-600 mb-1">
                            找不到對應新系統客戶的舊對象(顯示前 {s.unmatchedSample?.length ?? 0} /
                            {' '}{s.unmatchedPartyCount} 筆,需到客戶頁手動補名稱後重新預覽)
                          </div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b text-left text-slate-500">
                                <th className="px-2 py-1 w-28">舊系統編號</th>
                                <th className="px-2 py-1">公司全名</th>
                                <th className="px-2 py-1 w-40">公司簡稱</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(s.unmatchedSample ?? []).map((u) => (
                                <tr key={u.legacyCode} className="border-b last:border-b-0">
                                  <td className="px-2 py-1 font-mono">{u.legacyCode}</td>
                                  <td className="px-2 py-1">{u.fullName || '—'}</td>
                                  <td className="px-2 py-1">{u.simpleName || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="text-xs text-slate-500">
            勾選後按「執行匯入」。已存在 DB 的 code 不會被覆蓋。
            mdb 內偵測到 {preview.result.allTables.length} 個表,只處理上述 4 個對應目標。
          </div>
          <div>
            <button
              type="button"
              onClick={() => void onRun()}
              disabled={running || previewing || selected.size === 0}
              className="bg-emerald-600 text-white px-3 py-1.5 rounded hover:bg-emerald-700 disabled:opacity-50"
            >
              {running ? '匯入中…' : `執行匯入(${selected.size} 項)`}
            </button>
          </div>
        </div>
      ) : null}

      {runResult ? (
        <div className="grid gap-2 bg-white border rounded p-4">
          <div className="text-sm font-medium text-emerald-700">匯入完成</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs text-slate-600">
                <th className="px-2 py-1.5">項目</th>
                <th className="px-2 py-1.5 w-16 text-right">嘗試</th>
                <th className="px-2 py-1.5 w-16 text-right">新增</th>
                <th className="px-2 py-1.5 w-16 text-right">跳過</th>
                <th className="px-2 py-1.5 w-16 text-right">失敗</th>
              </tr>
            </thead>
            <tbody>
              {runResult.results.map((r) => (
                <tr key={r.target} className="border-b last:border-b-0">
                  <td className="px-2 py-1.5">{r.targetLabel}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{r.attempted}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700 font-medium">
                    {r.inserted}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{r.skipped}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {r.failed > 0 ? (
                      <span className="text-red-700">{r.failed}</span>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {runResult.results.some((r) => r.errors.length > 0) ? (
            <details className="text-xs text-slate-600">
              <summary className="cursor-pointer">部分錯誤訊息</summary>
              <ul className="pl-4 list-disc space-y-1 pt-1">
                {runResult.results.flatMap((r) =>
                  r.errors.map((e, i) => (
                    <li key={`${r.target}-${i}`} className="text-red-700">
                      <span className="text-slate-700">[{r.targetLabel}]</span> {e}
                    </li>
                  )),
                )}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}

      <HistorySection
        items={history}
        loading={historyLoading}
        expandedId={expandedId}
        onToggle={(id) => setExpandedId(expandedId === id ? null : id)}
        onRefresh={() => void loadHistory()}
      />
    </section>
  );
}

// ─── 歷史紀錄 section ──────────────────────────────────────────────

function HistorySection({
  items,
  loading,
  expandedId,
  onToggle,
  onRefresh,
}: {
  items: MdbImportRunRow[];
  loading: boolean;
  expandedId: number | null;
  onToggle: (id: number) => void;
  onRefresh: () => void;
}): JSX.Element {
  return (
    <div className="grid gap-2 bg-white border rounded p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">匯入紀錄</h3>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="text-xs border rounded px-2 py-1 text-slate-700 hover:bg-slate-100 disabled:opacity-50"
        >
          {loading ? '更新中…' : '重新整理'}
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 p-3">尚無匯入紀錄</div>
      ) : (
        <div className="border rounded">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs text-slate-600 border-b">
                <th className="px-3 py-1.5 w-8"></th>
                <th className="px-3 py-1.5 w-40">時間</th>
                <th className="px-3 py-1.5">檔名</th>
                <th className="px-3 py-1.5 w-24">執行人</th>
                <th className="px-3 py-1.5 w-20 text-right">嘗試</th>
                <th className="px-3 py-1.5 w-20 text-right">新增</th>
                <th className="px-3 py-1.5 w-20 text-right">跳過</th>
                <th className="px-3 py-1.5 w-20 text-right">失敗</th>
                <th className="px-3 py-1.5 w-16 text-center">狀態</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <RunRowAndDetail
                  key={r.id}
                  row={r}
                  expanded={expandedId === r.id}
                  onToggle={() => onToggle(r.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RunRowAndDetail({
  row,
  expanded,
  onToggle,
}: {
  row: MdbImportRunRow;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const statusBadge =
    row.status === 'ok'
      ? 'bg-emerald-100 text-emerald-700'
      : row.status === 'partial'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-red-100 text-red-700';
  return (
    <>
      <tr className="border-b last:border-b-0 cursor-pointer hover:bg-blue-50" onClick={onToggle}>
        <td className="px-3 py-1.5 text-slate-400">{expanded ? '▾' : '▸'}</td>
        <td className="px-3 py-1.5 text-xs text-slate-700">
          {new Date(row.startedAt).toLocaleString('zh-TW')}
        </td>
        <td className="px-3 py-1.5">
          <span className="font-mono text-xs">{row.filename}</span>
          <span className="text-slate-400 text-xs ml-1">({(row.fileSize / 1024 / 1024).toFixed(1)} MB)</span>
        </td>
        <td className="px-3 py-1.5 text-xs text-slate-600">{row.runByName ?? '—'}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">{row.totalAttempted}</td>
        <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700 font-medium">
          {row.totalInserted}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{row.totalSkipped}</td>
        <td className="px-3 py-1.5 text-right tabular-nums">
          {row.totalFailed > 0 ? (
            <span className="text-red-700">{row.totalFailed}</span>
          ) : (
            <span className="text-slate-400">0</span>
          )}
        </td>
        <td className="px-3 py-1.5 text-center">
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusBadge}`}>{row.status}</span>
        </td>
      </tr>
      {expanded ? (
        <tr className="bg-slate-50">
          <td colSpan={9} className="px-3 py-3">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b text-left text-slate-600">
                  <th className="px-2 py-1">舊系統表</th>
                  <th className="px-2 py-1 w-20 text-right">原始筆數</th>
                  <th className="px-2 py-1 w-20 text-right">匯入筆數</th>
                  <th className="px-2 py-1">對應新系統表</th>
                  <th className="px-2 py-1">對應轉檔項目</th>
                </tr>
              </thead>
              <tbody>
                {row.details.map((d, i) => {
                  const seq = String(i + 1).padStart(2, '0');
                  const insertedNote =
                    d.attempted !== d.inserted
                      ? ` (${d.inserted - d.attempted})`
                      : '';
                  return (
                    <tr key={d.target} className="border-b last:border-b-0">
                      <td className="px-2 py-1 font-medium">{d.sourceTable}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{d.sourceRowCount}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-emerald-700">
                        {d.inserted}
                        {insertedNote ? <span className="text-slate-400">{insertedNote}</span> : null}
                      </td>
                      <td className="px-2 py-1 font-mono uppercase">{d.target}</td>
                      <td className="px-2 py-1">
                        <span className="text-slate-400 mr-1">{seq}</span>
                        {d.targetLabel}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {row.details.some((d) => d.errors.length > 0) ? (
              <details className="text-xs text-slate-600 pt-2">
                <summary className="cursor-pointer">部分錯誤訊息</summary>
                <ul className="pl-4 list-disc space-y-1 pt-1">
                  {row.details.flatMap((d) =>
                    d.errors.map((e, i) => (
                      <li key={`${d.target}-${i}`} className="text-red-700">
                        <span className="text-slate-700">[{d.targetLabel}]</span> {e}
                      </li>
                    )),
                  )}
                </ul>
              </details>
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}
